/**
 * Funnel Utility Functions
 * 
 * Helper functions for creating, managing, and validating funnel data
 */

import { 
  FunnelCollection, 
  FunnelCollectionV2,
  FunnelCollectionType,
  MunicipalityFunnel, 
  SocialMediaFunnel, 
  EmailFunnel,
  EnhancedMunicipalityFunnel,
  EnhancedSocialMediaFunnel,
  EnhancedEmailFunnel,
  FunnelSession,
  FunnelEvent,
  SessionMetrics,
  MUNICIPALITY_FUNNEL_STEPS,
  SOCIAL_MEDIA_FUNNEL_STEPS,
  EMAIL_FUNNEL_STEPS,
  GEWERBE_FUNNEL_STEPS,
  TEILNEHMER_FUNNEL_STEPS,
  FunnelType,
  EventType,
  SessionStatus,
  FunnelIdPattern,
  BusinessType,
  generateFunnelId as generateFunnelIdFromTypes,
  parseFunnelId as parseFunnelIdFromTypes,
  isFunnelCollectionV2 as isFunnelCollectionV2FromTypes,
  FUNNEL_ID_PATTERNS
} from '@/types/funnel';

// Re-export these functions to fix import warnings
export const generateFunnelId = generateFunnelIdFromTypes;
export const parseFunnelId = parseFunnelIdFromTypes;
export const isFunnelCollectionV2 = isFunnelCollectionV2FromTypes;

/**
 * Generate a unique UUID for sessions and events
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Anonymize IP address by removing last octet
 */
export function anonymizeIP(ip: string): string {
  if (ip.includes('.')) {
    // IPv4
    const parts = ip.split('.');
    if (parts.length === 4) {
      return `${parts[0]}.${parts[1]}.${parts[2]}.xxx`;
    }
  } else if (ip.includes(':')) {
    // IPv6
    const parts = ip.split(':');
    if (parts.length >= 4) {
      return `${parts.slice(0, 6).join(':')}:xxxx`;
    }
  }
  return 'unknown';
}

/**
 * Get current timestamp in ISO 8601 format
 */
export function getCurrentTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Create initial funnel collection structure
 */
export function createInitialFunnelCollection(): FunnelCollection {
  const now = getCurrentTimestamp();
  
  return {
    gemeinden: {
      funnel_type: 'municipality_token',
      version: '1.0.0',
      created_at: now,
      updated_at: now,
      config: {
        retention_days: 90,
        anonymization_after_days: 30,
        session_timeout_minutes: 30
      },
      funnels: {}
    },
    social_media: {
      funnel_type: 'campaign_based',
      version: '1.0.0',
      created_at: now,
      updated_at: now,
      config: {
        retention_days: 60,
        anonymization_after_days: 14,
        session_timeout_minutes: 15
      },
      funnels: {}
    },
    email_campaigns: {
      funnel_type: 'email_based',
      version: '1.0.0',
      created_at: now,
      updated_at: now,
      config: {
        retention_days: 120,
        anonymization_after_days: 45,
        session_timeout_minutes: 60
      },
      funnels: {}
    }
  };
}

/**
 * Create a new municipality funnel
 */
export function createMunicipalityFunnel(
  token: string,
  municipalityId: string,
  municipalityName: string
): MunicipalityFunnel {
  const now = getCurrentTimestamp();
  
  return {
    funnel_type: 'municipality_token',
    version: '1.0.0',
    created_at: now,
    updated_at: now,
    config: {
      retention_days: 90,
      anonymization_after_days: 30,
      session_timeout_minutes: 30
    },
    municipality_id: municipalityId,
    municipality_name: municipalityName,
    token,
    funnel_steps: MUNICIPALITY_FUNNEL_STEPS,
    sessions: {}
  };
}

/**
 * Create a new social media funnel
 */
export function createSocialMediaFunnel(
  campaignId: string,
  campaignName: string,
  platform: 'instagram' | 'facebook' | 'linkedin' | 'twitter',
  campaignStart: string,
  campaignEnd: string
): SocialMediaFunnel {
  const now = getCurrentTimestamp();
  
  return {
    funnel_type: 'campaign_based',
    version: '1.0.0',
    created_at: now,
    updated_at: now,
    config: {
      retention_days: 60,
      anonymization_after_days: 14,
      session_timeout_minutes: 15
    },
    campaign_name: campaignName,
    platform,
    campaign_start: campaignStart,
    campaign_end: campaignEnd,
    funnel_steps: SOCIAL_MEDIA_FUNNEL_STEPS,
    sessions: {}
  };
}

/**
 * Create a new email funnel
 */
export function createEmailFunnel(
  campaignId: string,
  campaignName: string,
  emailProvider: 'resend' | 'mailchimp' | 'sendgrid',
  campaignDate: string
): EmailFunnel {
  const now = getCurrentTimestamp();
  
  return {
    funnel_type: 'email_based',
    version: '1.0.0',
    created_at: now,
    updated_at: now,
    config: {
      retention_days: 120,
      anonymization_after_days: 45,
      session_timeout_minutes: 60
    },
    campaign_name: campaignName,
    email_provider: emailProvider,
    campaign_date: campaignDate,
    funnel_steps: EMAIL_FUNNEL_STEPS,
    sessions: {}
  };
}

/**
 * Create a new funnel event
 */
export function createFunnelEvent(
  eventType: EventType,
  stepId: string,
  data: Record<string, any> = {}
): FunnelEvent {
  return {
    event_id: generateUUID(),
    timestamp: getCurrentTimestamp(),
    event_type: eventType,
    step_id: stepId,
    data
  };
}

/**
 * Create initial session metrics
 */
export function createInitialSessionMetrics(): SessionMetrics {
  return {
    total_duration_ms: 0,
    steps_completed: 0,
    interactions_count: 0,
    conversion_score: 0
  };
}

/**
 * Calculate session metrics based on events
 */
export function calculateSessionMetrics(
  events: FunnelEvent[],
  createdAt: string
): SessionMetrics {
  const completedSteps = new Set<string>();
  let interactionCount = 0;
  let conversionScore = 0;
  
  // Process events to calculate metrics
  events.forEach(event => {
    if (event.event_type === 'step_complete') {
      completedSteps.add(event.step_id);
    } else if (event.event_type === 'interaction') {
      interactionCount++;
    } else if (event.event_type === 'conversion') {
      conversionScore = 1.0;
    }
  });
  
  // Calculate total duration
  const created = new Date(createdAt).getTime();
  const now = Date.now();
  const totalDuration = now - created;
  
  // Calculate engagement-based conversion score if no explicit conversion
  if (conversionScore === 0) {
    const engagementScore = Math.min(
      (completedSteps.size / 5) * 0.5 + // Step completion (50% weight)
      (Math.min(interactionCount, 10) / 10) * 0.3 + // Interactions (30% weight)
      (Math.min(totalDuration / (5 * 60 * 1000), 1)) * 0.2, // Time spent (20% weight)
      1.0
    );
    conversionScore = engagementScore;
  }
  
  return {
    total_duration_ms: totalDuration,
    steps_completed: completedSteps.size,
    interactions_count: interactionCount,
    conversion_score: conversionScore
  };
}

/**
 * Determine device type from user agent
 */
export function getDeviceType(userAgent: string): 'desktop' | 'mobile' | 'tablet' {
  const ua = userAgent.toLowerCase();
  
  if (/tablet|ipad|playbook|silk/.test(ua)) {
    return 'tablet';
  } else if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/.test(ua)) {
    return 'mobile';
  } else {
    return 'desktop';
  }
}

/**
 * Parse and compress user agent to a more compact format
 * 
 * Examples:
 * - "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36"
 *   => "Chrome/120.0|Windows 10|Desktop"
 * 
 * - "Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1"
 *   => "Safari/17.0|iOS 17.0|Mobile"
 */
export function compressUserAgent(userAgent: string): string {
  if (!userAgent || userAgent === 'unknown') {
    return 'Unknown';
  }

  const ua = userAgent.toLowerCase();
  let browser = 'Unknown';
  let browserVersion = '';
  let os = 'Unknown';
  let osVersion = '';
  
  // Detect browser and version
  if (ua.includes('edg/')) {
    const match = ua.match(/edg\/([\d.]+)/);
    browser = 'Edge';
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('chrome/') && !ua.includes('edg/')) {
    const match = ua.match(/chrome\/([\d.]+)/);
    browser = 'Chrome';
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('firefox/')) {
    const match = ua.match(/firefox\/([\d.]+)/);
    browser = 'Firefox';
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('safari/') && !ua.includes('chrome')) {
    const match = ua.match(/version\/([\d.]+)/);
    browser = 'Safari';
    browserVersion = match ? match[1] : '';
  } else if (ua.includes('opera/') || ua.includes('opr/')) {
    const match = ua.match(/(?:opera|opr)\/([\d.]+)/);
    browser = 'Opera';
    browserVersion = match ? match[1] : '';
  }
  
  // Simplify browser version (only major.minor)
  if (browserVersion) {
    const parts = browserVersion.split('.');
    browserVersion = parts.slice(0, 2).join('.');
  }
  
  // Detect OS and version
  if (ua.includes('windows nt')) {
    os = 'Windows';
    const versionMatch = ua.match(/windows nt ([\d.]+)/);
    if (versionMatch) {
      const ntVersion = versionMatch[1];
      osVersion = ntVersion === '10.0' ? '10' : 
                  ntVersion === '6.3' ? '8.1' : 
                  ntVersion === '6.2' ? '8' : 
                  ntVersion === '6.1' ? '7' : ntVersion;
    }
  } else if (ua.includes('iphone') || ua.includes('ipad')) {
    // iOS devices report as iPhone/iPad, not macOS
    os = 'iOS';
    const versionMatch = ua.match(/os ([\d_]+)/);
    if (versionMatch) {
      osVersion = versionMatch[1].replace(/_/g, '.');
    }
  } else if (ua.includes('mac os x')) {
    os = 'macOS';
    const versionMatch = ua.match(/mac os x ([\d_]+)/);
    if (versionMatch) {
      osVersion = versionMatch[1].replace(/_/g, '.');
    }
  } else if (ua.includes('android')) {
    os = 'Android';
    const versionMatch = ua.match(/android ([\d.]+)/);
    if (versionMatch) {
      osVersion = versionMatch[1];
    }
  } else if (ua.includes('linux')) {
    os = 'Linux';
  }
  
  // Get device type
  const deviceType = getDeviceType(userAgent);
  const deviceLabel = deviceType.charAt(0).toUpperCase() + deviceType.slice(1);
  
  // Build compact string
  const parts = [];
  
  // Browser/Version
  parts.push(browserVersion ? `${browser}/${browserVersion}` : browser);
  
  // OS/Version
  parts.push(osVersion ? `${os} ${osVersion}` : os);
  
  // Device type
  parts.push(deviceLabel);
  
  // Join with pipe separator
  return parts.join('|');
}

/**
 * Extract key user agent information for analytics
 * Returns a structured object with parsed UA data
 */
export function parseUserAgent(userAgent: string): {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  device: 'desktop' | 'mobile' | 'tablet';
  isBot: boolean;
} {
  const ua = userAgent.toLowerCase();
  const result = {
    browser: 'Unknown',
    browserVersion: '',
    os: 'Unknown', 
    osVersion: '',
    device: getDeviceType(userAgent),
    isBot: false
  };
  
  // Check for bots
  if (/bot|crawler|spider|crawling|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|twitterbot|linkedinbot|whatsapp|telegram/i.test(ua)) {
    result.isBot = true;
  }
  
  // Parse browser
  if (ua.includes('edg/')) {
    result.browser = 'Edge';
    const match = ua.match(/edg\/([\d.]+)/);
    if (match) result.browserVersion = match[1];
  } else if (ua.includes('chrome/') && !ua.includes('edg/')) {
    result.browser = 'Chrome';
    const match = ua.match(/chrome\/([\d.]+)/);
    if (match) result.browserVersion = match[1];
  } else if (ua.includes('firefox/')) {
    result.browser = 'Firefox';
    const match = ua.match(/firefox\/([\d.]+)/);
    if (match) result.browserVersion = match[1];
  } else if (ua.includes('safari/') && !ua.includes('chrome')) {
    result.browser = 'Safari';
    const match = ua.match(/version\/([\d.]+)/);
    if (match) result.browserVersion = match[1];
  }
  
  // Parse OS
  if (ua.includes('windows nt')) {
    result.os = 'Windows';
    const match = ua.match(/windows nt ([\d.]+)/);
    if (match) {
      const nt = match[1];
      result.osVersion = nt === '10.0' ? '10' : 
                        nt === '6.3' ? '8.1' : 
                        nt === '6.2' ? '8' : 
                        nt === '6.1' ? '7' : nt;
    }
  } else if (ua.includes('iphone') || ua.includes('ipad')) {
    result.os = 'iOS';
    const match = ua.match(/os ([\d_]+)/);
    if (match) result.osVersion = match[1].replace(/_/g, '.');
  } else if (ua.includes('mac os x')) {
    result.os = 'macOS';
    const match = ua.match(/mac os x ([\d_]+)/);
    if (match) result.osVersion = match[1].replace(/_/g, '.');
  } else if (ua.includes('android')) {
    result.os = 'Android';
    const match = ua.match(/android ([\d.]+)/);
    if (match) result.osVersion = match[1];
  } else if (ua.includes('linux')) {
    result.os = 'Linux';
  }
  
  return result;
}

/**
 * Get viewport dimensions from window or fallback
 */
export function getViewportDimensions(): string {
  if (typeof window !== 'undefined') {
    return `${window.innerWidth}x${window.innerHeight}`;
  }
  return 'unknown';
}

/**
 * Validate funnel event data
 */
export function validateFunnelEvent(event: FunnelEvent): boolean {
  const requiredFields = ['event_id', 'timestamp', 'event_type', 'step_id'];
  
  for (const field of requiredFields) {
    if (!(field in event) || event[field as keyof FunnelEvent] === undefined) {
      return false;
    }
  }
  
  const validEventTypes: EventType[] = ['step_enter', 'step_complete', 'step_exit', 'interaction', 'conversion', 'dropout'];
  if (!validEventTypes.includes(event.event_type)) {
    return false;
  }
  
  return true;
}

/**
 * Clean up old sessions based on retention policy
 */
export function shouldCleanupSession(
  sessionCreatedAt: string,
  retentionDays: number
): boolean {
  const created = new Date(sessionCreatedAt).getTime();
  const now = Date.now();
  const retentionMs = retentionDays * 24 * 60 * 60 * 1000;
  
  return (now - created) > retentionMs;
}

/**
 * Check if session should be anonymized
 */
export function shouldAnonymizeSession(
  sessionCreatedAt: string,
  anonymizationAfterDays: number
): boolean {
  const created = new Date(sessionCreatedAt).getTime();
  const now = Date.now();
  const anonymizationMs = anonymizationAfterDays * 24 * 60 * 60 * 1000;
  
  return (now - created) > anonymizationMs;
}

/**
 * Check if session is timed out
 */
export function isSessionTimedOut(
  sessionUpdatedAt: string,
  timeoutMinutes: number
): boolean {
  const updated = new Date(sessionUpdatedAt).getTime();
  const now = Date.now();
  const timeoutMs = timeoutMinutes * 60 * 1000;
  
  return (now - updated) > timeoutMs;
}

/**
 * Get session status based on events and timeouts
 */
export function getSessionStatus(
  events: FunnelEvent[],
  updatedAt: string,
  timeoutMinutes: number
): SessionStatus {
  // Check for explicit conversion
  const hasConversion = events.some(e => e.event_type === 'conversion');
  if (hasConversion) {
    return 'converted';
  }
  
  // Check for explicit dropout
  const hasDropout = events.some(e => e.event_type === 'dropout');
  if (hasDropout) {
    return 'abandoned';
  }
  
  // Check for timeout
  if (isSessionTimedOut(updatedAt, timeoutMinutes)) {
    return 'abandoned';
  }
  
  // Check for completion (all required steps completed)
  const completedSteps = events
    .filter(e => e.event_type === 'step_complete')
    .map(e => e.step_id);
  
  // This would need to be enhanced with actual funnel step requirements
  // For now, assume completion if callback step is completed
  if (completedSteps.includes('callback_request')) {
    return 'completed';
  }
  
  return 'active';
}

/**
 * Extract UTM parameters from URL
 */
export function extractUTMParameters(url: string): Record<string, string> {
  const urlObj = new URL(url);
  const params: Record<string, string> = {};
  
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  
  utmParams.forEach(param => {
    const value = urlObj.searchParams.get(param);
    if (value) {
      params[param] = value;
    }
  });
  
  return params;
}

/**
 * Create initial V2 funnel collection structure
 */
export function createInitialFunnelCollectionV2(): FunnelCollectionV2 {
  const now = getCurrentTimestamp();
  
  return {
    gemeinden: {
      funnel_type: 'gemeinden_service',
      version: '2.0.0',
      created_at: now,
      updated_at: now,
      config: {
        retention_days: 90,
        anonymization_after_days: 30,
        session_timeout_minutes: 30
      },
      funnels: {}
    },
    gewerbe: {
      funnel_type: 'business_service',
      version: '2.0.0',
      created_at: now,
      updated_at: now,
      config: {
        retention_days: 120,
        anonymization_after_days: 45,
        session_timeout_minutes: 60
      },
      funnels: {}
    },
    teilnehmer: {
      funnel_type: 'participant_service',
      version: '2.0.0',
      created_at: now,
      updated_at: now,
      config: {
        retention_days: 90,
        anonymization_after_days: 30,
        session_timeout_minutes: 30
      },
      funnels: {}
    },
    session_mappings: {
      consultation_by_token: {},
      agreement_by_session: {}
    },
    compliance_logs: {
      data_exports: {},
      deletions: {}
    }
  };
}

/**
 * Create a new enhanced municipality funnel with V2 schema
 */
export function createEnhancedMunicipalityFunnel(
  businessType: BusinessType,
  token: string,
  municipalityId: string,
  municipalityName: string
): EnhancedMunicipalityFunnel {
  const now = getCurrentTimestamp();
  const funnelId = generateFunnelId(businessType, 'gemeinde', municipalityId);
  
  // Choose appropriate funnel steps based on business type
  let funnelSteps = MUNICIPALITY_FUNNEL_STEPS;
  if (businessType === 'gewerbe') {
    funnelSteps = GEWERBE_FUNNEL_STEPS;
  } else if (businessType === 'teilnehmer') {
    funnelSteps = TEILNEHMER_FUNNEL_STEPS;
  }

  return {
    funnel_type: 'municipality_token',
    funnel_id: funnelId,
    version: '2.0.0',
    created_at: now,
    updated_at: now,
    config: {
      retention_days: 90,
      anonymization_after_days: 30,
      session_timeout_minutes: 30
    },
    municipality_id: municipalityId,
    municipality_name: municipalityName,
    token,
    business_type: businessType,
    funnel_steps: funnelSteps,
    sessions: {}
  };
}

/**
 * Create a new enhanced social media funnel with V2 schema
 */
export function createEnhancedSocialMediaFunnel(
  businessType: BusinessType,
  campaignId: string,
  campaignName: string,
  platform: 'instagram' | 'facebook' | 'linkedin' | 'twitter',
  campaignStart: string,
  campaignEnd: string
): EnhancedSocialMediaFunnel {
  const now = getCurrentTimestamp();
  const funnelId = generateFunnelId(businessType, 'social', campaignId);
  
  return {
    funnel_type: 'campaign_based',
    funnel_id: funnelId,
    version: '2.0.0',
    created_at: now,
    updated_at: now,
    config: {
      retention_days: 60,
      anonymization_after_days: 14,
      session_timeout_minutes: 15
    },
    campaign_name: campaignName,
    platform,
    campaign_start: campaignStart,
    campaign_end: campaignEnd,
    business_type: businessType,
    funnel_steps: SOCIAL_MEDIA_FUNNEL_STEPS,
    sessions: {}
  };
}

/**
 * Create a new enhanced email funnel with V2 schema
 */
export function createEnhancedEmailFunnel(
  businessType: BusinessType,
  campaignId: string,
  campaignName: string,
  emailProvider: 'resend' | 'mailchimp' | 'sendgrid',
  campaignDate: string
): EnhancedEmailFunnel {
  const now = getCurrentTimestamp();
  const funnelId = generateFunnelId(businessType, 'email', campaignId);
  
  return {
    funnel_type: 'email_based',
    funnel_id: funnelId,
    version: '2.0.0',
    created_at: now,
    updated_at: now,
    config: {
      retention_days: 120,
      anonymization_after_days: 45,
      session_timeout_minutes: 60
    },
    campaign_name: campaignName,
    email_provider: emailProvider,
    campaign_date: campaignDate,
    business_type: businessType,
    funnel_steps: EMAIL_FUNNEL_STEPS,
    sessions: {}
  };
}

/**
 * Migrate session from legacy format to V2 format
 */
export function migrateSessionToV2(
  legacySessionId: string,
  legacySession: any,
  businessType: BusinessType = 'gemeinden'
): { sessionId: string; session: any } {
  // Keep existing session structure but ensure it's properly typed
  const migratedSession = {
    ...legacySession,
    business_type: businessType,
    migrated_at: getCurrentTimestamp(),
    migration_version: '2.0.0'
  };
  
  return {
    sessionId: legacySessionId,
    session: migratedSession
  };
}

/**
 * Get collection type from collection data
 */
export function getCollectionType(collection: FunnelCollectionType): 'v1' | 'v2' {
  return isFunnelCollectionV2(collection) ? 'v2' : 'v1';
}

/**
 * Validate funnel ID pattern
 */
export function isValidFunnelIdPattern(funnelId: string): funnelId is FunnelIdPattern {
  const pattern = /^(gemeinden|gewerbe|teilnehmer)_(gemeinde|business|participant|social|email)_.+$/;
  return pattern.test(funnelId);
}